import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  allowNavigation?: boolean;
  className?: string;
}

export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  allowNavigation = false,
  className,
}) => {
  const handleStepClick = (index: number) => {
    if (allowNavigation && onStepClick && index < currentStep) {
      onStepClick(index);
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = allowNavigation && index < currentStep;

          return (
            <React.Fragment key={step.id}>
              {/* Step Circle */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => handleStepClick(index)}
                  disabled={!isClickable}
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200',
                    isCompleted && 'bg-primary-500 text-white',
                    isCurrent && 'bg-primary-500 text-white ring-4 ring-primary-500/30',
                    !isCompleted && !isCurrent && 'bg-surface-700 text-surface-400',
                    isClickable && 'cursor-pointer hover:bg-primary-400',
                    !isClickable && !isCurrent && 'cursor-default'
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                <div className="mt-2 text-center">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      (isCompleted || isCurrent) ? 'text-white' : 'text-surface-400'
                    )}
                  >
                    {step.title}
                  </p>
                  {step.description && (
                    <p className="text-xs text-surface-500 mt-0.5 max-w-[120px]">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-4 transition-colors duration-200',
                    index < currentStep ? 'bg-primary-500' : 'bg-surface-700'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Wizard Container Component
interface WizardContainerProps {
  steps: WizardStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onComplete: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  isValid?: boolean;
  isLoading?: boolean;
  allowBackNavigation?: boolean;
  completeButtonText?: string;
  nextButtonText?: string;
  backButtonText?: string;
  cancelButtonText?: string;
}

export const WizardContainer: React.FC<WizardContainerProps> = ({
  steps,
  currentStep,
  onStepChange,
  onComplete,
  onCancel,
  children,
  isValid = true,
  isLoading = false,
  allowBackNavigation = true,
  completeButtonText = 'Concluir',
  nextButtonText = 'Próximo',
  backButtonText = 'Voltar',
  cancelButtonText = 'Cancelar',
}) => {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      onStepChange(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      onStepChange(currentStep - 1);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Stepper Header */}
      <div className="px-6 py-4 border-b border-surface-700">
        <WizardStepper
          steps={steps}
          currentStep={currentStep}
          onStepClick={allowBackNavigation ? onStepChange : undefined}
          allowNavigation={allowBackNavigation}
        />
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto p-6">
        {children}
      </div>

      {/* Navigation Footer */}
      <div className="px-6 py-4 border-t border-surface-700 flex items-center justify-between">
        <div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-surface-400 hover:text-white transition-colors"
            >
              {cancelButtonText}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isFirstStep && (
            <button
              type="button"
              onClick={handleBack}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-surface-300 bg-surface-700 rounded-lg hover:bg-surface-600 transition-colors disabled:opacity-50"
            >
              {backButtonText}
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={!isValid || isLoading}
            className={cn(
              'px-6 py-2 text-sm font-medium rounded-lg transition-all',
              'bg-primary-500 text-white hover:bg-primary-400',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isLoading && 'animate-pulse'
            )}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processando...
              </span>
            ) : (
              isLastStep ? completeButtonText : nextButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepper;
